import React, { useState, useEffect } from 'react';
import { Bot, Mic2, FileAudio, Wand2, Brain, Globe, MessageSquare, Sparkles, Radio, Share2, Code, Zap, Building2, Users, Award } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { Language } from '../translations';

interface ProcessExplanationProps {
  loading: boolean;
  language: Language;
}

interface CountdownProps {
  duration: number;
  onComplete: () => void;
}

const slides = [
  {
    icon: <Globe className="w-8 h-8 text-orange-500" />,
    titleKey: 'webAnalysis',
    descriptionKey: 'webAnalysisDesc'
  },
  {
    icon: <Brain className="w-8 h-8 text-orange-500" />,
    titleKey: 'aiProcessing',
    descriptionKey: 'aiProcessingDesc'
  },
  {
    icon: <MessageSquare className="w-8 h-8 text-orange-500" />,
    titleKey: 'scriptGen',
    descriptionKey: 'scriptGenDesc'
  },
  {
    icon: <Sparkles className="w-8 h-8 text-orange-500" />,
    titleKey: 'voiceOpt',
    descriptionKey: 'voiceOptDesc'
  },
  {
    icon: <Radio className="w-8 h-8 text-orange-500" />,
    titleKey: 'audioSynth',
    descriptionKey: 'audioSynthDesc'
  },
  {
    icon: <Share2 className="w-8 h-8 text-orange-500" />,
    titleKey: 'p3Tech',
    descriptionKey: 'p3TechDesc'
  },
  {
    icon: <Code className="w-8 h-8 text-orange-500" />,
    titleKey: 'optimization',
    descriptionKey: 'optimizationDesc'
  },
  {
    icon: <Zap className="w-8 h-8 text-orange-500" />,
    titleKey: 'qualityControl',
    descriptionKey: 'qualityControlDesc'
  },
  {
    icon: <Building2 className="w-8 h-8 text-orange-500" />,
    titleKey: 'p3Platform',
    descriptionKey: 'p3PlatformDesc'
  },
  {
    icon: <Users className="w-8 h-8 text-orange-500" />,
    titleKey: 'expertise',
    descriptionKey: 'expertiseDesc'
  },
  {
    icon: <Bot className="w-8 h-8 text-orange-500" />,
    titleKey: 'aiAssist',
    descriptionKey: 'aiAssistDesc'
  },
  {
    icon: <Mic2 className="w-8 h-8 text-orange-500" />,
    titleKey: 'voiceProd',
    descriptionKey: 'voiceProdDesc'
  },
  {
    icon: <FileAudio className="w-8 h-8 text-orange-500" />,
    titleKey: 'audioExport',
    descriptionKey: 'audioExportDesc'
  },
  {
    icon: <Wand2 className="w-8 h-8 text-orange-500" />,
    titleKey: 'finalTouch',
    descriptionKey: 'finalTouchDesc'
  },
  {
    icon: <Award className="w-8 h-8 text-orange-500" />,
    titleKey: 'qualityGuarantee',
    descriptionKey: 'qualityGuaranteeDesc'
  }
];

const Countdown = ({ duration, onComplete }: CountdownProps) => {
  const [timeLeft, setTimeLeft] = useState(duration);

  useEffect(() => {
    if (!duration) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [duration, onComplete]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const progress = ((duration - timeLeft) / duration) * 100;

  return (
    <div className="relative w-32 h-32 mx-auto mb-6">
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="64"
          cy="64"
          r="60"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-orange-100 dark:text-orange-900"
        />
        <circle
          cx="64"
          cy="64"
          r="60"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeDasharray={2 * Math.PI * 60}
          strokeDashoffset={2 * Math.PI * 60 * (1 - progress / 100)}
          className="text-orange-500 transition-all duration-1000 ease-linear"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-2xl font-bold text-orange-500">
          {`${minutes}:${seconds.toString().padStart(2, '0')}`}
        </div>
      </div>
    </div>
  );
};

export function ProcessExplanation({ loading, language }: ProcessExplanationProps) {
  const { t } = useTranslation(language);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showSlide, setShowSlide] = useState(false);

  useEffect(() => {
    if (!loading) {
      setCurrentSlideIndex(0);
      setShowSlide(false);
      return;
    }

    setShowSlide(true);
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
    }, 10000); // Change slide every 10 seconds

    return () => clearInterval(interval);
  }, [loading]);

  if (!loading || !showSlide) return null;

  const currentSlide = slides[currentSlideIndex];

  return (
    <div className="mt-8 space-y-6">
      <Countdown duration={120} onComplete={() => {}} />
      
      <div className={`p-6 rounded-lg bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-900 shadow-lg slide-up`}>
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full">
            {currentSlide.icon}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-orange-500">
              {t('processExplanation', currentSlide.titleKey)}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {t('processExplanation', currentSlide.descriptionKey)}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-center space-x-2">
        {slides.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlideIndex
                ? 'bg-orange-500 w-4'
                : 'bg-orange-200 dark:bg-orange-900'
            }`}
          />
        ))}
      </div>
    </div>
  );
}